import React, {useState} from "react";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {PoliciesListingContent as SharedPoliciesListing} from "@/customComponents/globalComponents/policiesContent.jsx";
import {CancellationModificationRefundContent} from "../Components/cancellationModificationRefund.jsx";
import {PassengerPoliciesContent} from "./passengerPolicies.jsx";
import {PickupPoliciesContent} from "./pickupPolicies.jsx";
import {TollPoliciesContent} from "./tollPolicies.jsx";
import {PoliciesExtraDetailsContent} from "./policiesExtraDetails.jsx";
import {storage, STORAGE_KEYS} from "@/utils/storage";

// The two policy rows that now open the CMR popup instead of their old single-topic
// step, keyed by getHopnPolicies() page_name → the CMR tab to preselect.
const CMR_ROWS = {
    cancellationPolicies: 'cancellation',
    refundPolicies: 'refund',
};

// Non-CMR policy rows → their content component. On desktop these open as an inline
// .upper-modal STACKED OVER the list (the "For A Smooth Journey" list stays mounted
// behind them), matching how the CMR row already behaves — instead of navigating to a
// separate step that removes and later re-creates the list.
const POLICY_POPUP_CONTENT = {
    passengerPolicies: PassengerPoliciesContent,
    pickupPolicies: PickupPoliciesContent,
    tollPolicies: TollPoliciesContent,
    policiesExtraDetails: PoliciesExtraDetailsContent,
};

// Booking-flow adapter: the listing's page keys are the booking step names, so
// onOpenStep maps straight onto setStep — except the Cancellation & Refund rows,
// which open the CMR view. Desktop opens each row as an inline .upper-modal popup
// (via onCmr / onPolicy) so the user can close it and return to the list; mobile
// routes to the stacked-overlay step (its ✕ pops back).
export function PoliciesListingContent({setStep, onCmr, onPolicy}) {
    const openStep = (pageName) => {
        const cmrTab = CMR_ROWS[pageName];
        if (cmrTab) {
            if (onCmr && window.innerWidth >= 576) {
                onCmr(cmrTab);
            } else {
                storage.set(STORAGE_KEYS.CMR_INITIAL_TAB, cmrTab);
                setStep('cancellationModificationRefund');
            }
            return;
        }
        // Non-CMR rows: desktop stacks the content as an inline popup over the list;
        // mobile keeps the full-screen stacked-overlay step.
        if (onPolicy && window.innerWidth >= 576 && POLICY_POPUP_CONTENT[pageName]) {
            onPolicy(pageName);
            return;
        }
        setStep(pageName);
    };
    return <SharedPoliciesListing onOpenStep={openStep}/>;
}

export function PoliciesListing({setStep, closeModal}) {
    // Which CMR tab is open as a desktop popup — null when closed.
    const [cmrPopup, setCmrPopup] = useState(null); // null | 'cancellation' | 'refund'
    // Which non-CMR policy is open as a desktop popup — null when closed.
    const [policyPopup, setPolicyPopup] = useState(null); // null | page_name
    const PolicyContent = policyPopup ? POLICY_POPUP_CONTENT[policyPopup] : null;
    return (
        <>
            {/* CMR popup (desktop) — overlays the policies list so the user can close it
                and return, instead of the CMR replacing this step with no back button. */}
            {cmrPopup && (
                <div className="upper-modal" style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true} title="" buttonStyle="elegant"
                            extraClasses="" totalSteps={0} cancelButton={true} cancelButtonType="icon"
                            backStep={null} showBackButton={false}
                            setStep={setStep} closeModal={() => setCmrPopup(null)}
                        />
                        <div className="upper-modal-content padding-bottom-30">
                            <div className="modalPages-spacing"><CancellationModificationRefundContent initialTab={cmrPopup}/></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Policy popup (desktop) — same stack-on-top treatment for the passenger /
                pickup / toll / "More" rows, so the list stays mounted behind them. */}
            {PolicyContent && (
                <div className="upper-modal" style={{display: 'flex'}}>
                    <div className="upper-modal-inner">
                        <ModalHeader
                            inline={true} title="" extraClasses="less-space" totalSteps={0}
                            cancelButton={true} cancelButtonType="icon"
                            backStep={null} showBackButton={false}
                            setStep={setStep} closeModal={() => setPolicyPopup(null)}
                        />
                        <div className="upper-modal-content padding-bottom-30">
                            <div className="modalPages-spacing"><PolicyContent/></div>
                        </div>
                    </div>
                </div>
            )}

            <ModalHeader title="" extraClasses="" totalSteps={0} cancelButton={false} backStep="orderDetails" showBackButton={true} setStep={setStep} closeModal={closeModal}/>
            <PoliciesListingContent setStep={setStep} onCmr={setCmrPopup} onPolicy={setPolicyPopup}/>
        </>
    );
}
export default PoliciesListing;
